import type { Profile } from './types';

type AuthorLike = Pick<Profile, 'handle' | 'display_name'> | null | undefined;

/**
 * The public identity for a member - the handle - shown everywhere except
 * their own profile: posts, comments, bids, redesign submissions. The full
 * name (display_name) is profile-only.
 *
 * Returned WITHOUT a leading '@' so it's safe for Avatar initials and alt/aria
 * text; prefix '@' yourself in visible labels.
 *
 * Handle is required at onboarding, so the display_name fallback only covers
 * legacy rows created before handles existed.
 */
export function authorHandle(author: AuthorLike, fallback = 'member'): string {
  return author?.handle?.trim() || author?.display_name?.trim() || fallback;
}

/**
 * Route to a member's public profile (/u/:handle). Falls back to the user id
 * when no handle is set - PublicProfile accepts a UUID path segment too.
 * Returns null when there's no author to link to.
 */
export function profilePath(
  author: (Pick<Profile, 'handle'> & { id?: string | null }) | null | undefined,
): string | null {
  if (!author) return null;
  const h = author.handle?.trim();
  if (h) return `/u/${h}`;
  return author.id ? `/u/${author.id}` : null;
}
